import React, { FC } from "react";
import { useQuery } from "@tanstack/react-query";
import { Modal } from "../../widgets/modal";
import Loader from "../../widgets/Loader";
import {
  confirmPriorityScheduled,
  fetchScheduledPriority,
} from "./scheduling.utils";
import Swal from "sweetalert2";
import { SCHEDULING_DIALOGS } from "../../constants/dialog.constants";

type SchedulingPriorityModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void | Promise<void>;
  scheduledId: number;
  propertyName?: string;
};

const isSuccessfulResponse = (result: any) =>
  result?.messages?.[0]?.code === "0";

const getFailureMessage = (result: any) =>
  result?.messages?.[0]?.message ||
  result?.message ||
  SCHEDULING_DIALOGS.priorityConfirmFailed;

const SchedulingPriorityModal: FC<SchedulingPriorityModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  scheduledId,
  propertyName,
}) => {
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const { data: priorityDescription, isFetching, error } = useQuery({
    queryKey: ["SCHEDULED_PRIORITY"],
    queryFn: fetchScheduledPriority,
    enabled: isOpen,
    refetchOnWindowFocus: false,
    staleTime: Infinity,
    gcTime: Infinity,
  });

  const descriptionText =
    priorityDescription?.trim() || SCHEDULING_DIALOGS.priorityNoDescription;

  const handleClose = () => {
    onClose();
  };

  const handleConfirmPriority = async () => {
    if (!scheduledId) {
      Swal.fire({
        customClass: ".swal2-confirm",
        icon: "warning",
        title: SCHEDULING_DIALOGS.priorityMissingJob,
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await confirmPriorityScheduled(scheduledId);
      if (isSuccessfulResponse(result)) {
        Swal.fire({
          customClass: {
            popup: "sweetAlert",
          },
          icon: "success",
          showConfirmButton: false,
          timer: 1500,
          title: SCHEDULING_DIALOGS.priorityConfirmed,
        });
        await onSubmit();
        onClose();
      } else {
        Swal.fire({
          customClass: ".swal2-confirm",
          icon: "warning",
          title: getFailureMessage(result),
        });
      }
    } catch (confirmError) {
      console.error(SCHEDULING_DIALOGS.priorityConfirmFailed, confirmError);
      Swal.fire({
        customClass: ".swal2-confirm",
        icon: "warning",
        title: SCHEDULING_DIALOGS.priorityConfirmFailed,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      show={isOpen}
      label={`${SCHEDULING_DIALOGS.priorityLabel}${propertyName ? ` (${propertyName})` : ""}`}
      onClose={handleClose}
      className="custom-modal scheduling-ready-for-mulch-modal scheduling-status-confirmation-modal scheduling-priority-modal"
    >
      <div className="requestModalSection">
        <div className="modalForm">
          {isFetching || isSubmitting ? <Loader /> : null}
          {error ? (
            <div className="text-danger">
              {SCHEDULING_DIALOGS.priorityLoadFailed}
            </div>
          ) : (
            <div className="scheduling-description-content">
              <div className="scheduling-description-block">
                <div className="scheduling-description-text scheduling-status-popup-text">
                  {descriptionText}
                </div>
              </div>
            </div>
          )}
          <div className="scheduling-priority-footer">
            <div className="scheduling-modal-actions scheduling-priority-actions">
              <button
                type="button"
                className="btn cancelPwdBtn"
                onClick={handleClose}
              >
                {SCHEDULING_DIALOGS.closeButton}
              </button>
              <button
                type="button"
                className="btn submitBtn"
                onClick={handleConfirmPriority}
                disabled={isSubmitting}
              >
                {SCHEDULING_DIALOGS.confirmPriority}
              </button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SchedulingPriorityModal;
